<template>
  <div class="ps-page">
    <!-- ============ TOP TICKER ============ -->
    <div class="landing-ticker marquee">
      <div class="ticker">
        <template v-for="i in 6">
          <span :key="i">{{ $t("landing.ticker") }}</span>
          <v-icon :key="'s' + i" size="14" class="landing-ticker__star"
            >fal fa-star</v-icon
          >
        </template>
      </div>
    </div>

    <!-- ============ NAV ============ -->
    <header class="landing-nav">
      <nav class="wrap landing-nav__inner">
        <router-link to="/" class="landing-nav__logo">
          <img src="/images/pos.png" alt="PosSmart" class="landing-nav__mark" />
          <span class="landing-nav__name">PosSmart</span>
        </router-link>

        <div class="landing-nav__links">
          <a href="#features" class="landing-nav__link">{{
            $t("landing.nav.features")
          }}</a>
          <a href="#business" class="landing-nav__link">{{
            $t("landing.nav.business")
          }}</a>
          <a href="#screens" class="landing-nav__link">{{
            $t("landing.nav.screens")
          }}</a>
          <a href="#packages" class="landing-nav__link">{{
            $t("landing.nav.packages")
          }}</a>
          <a href="#reviews" class="landing-nav__link">{{
            $t("landing.nav.reviews")
          }}</a>
        </div>

        <div class="landing-nav__cta">
          <router-link to="/login" class="landing-nav__login">{{
            $t("landing.nav.login")
          }}</router-link>
          <router-link to="/register" class="ps-btn ps-btn--lime landing-nav__register">{{
            $t("landing.nav.register")
          }}</router-link>
        </div>

        <button
          class="landing-nav__burger"
          :aria-label="'Menu'"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <v-icon size="20">fal fa-bars</v-icon>
        </button>
      </nav>

      <div
        v-if="mobileMenuOpen"
        class="landing-nav__mobile"
        @click="mobileMenuOpen = false"
      >
        <a href="#features" class="landing-nav__mobile-link">{{
          $t("landing.nav.features")
        }}</a>
        <a href="#business" class="landing-nav__mobile-link">{{
          $t("landing.nav.business")
        }}</a>
        <a href="#screens" class="landing-nav__mobile-link">{{
          $t("landing.nav.screens")
        }}</a>
        <a href="#packages" class="landing-nav__mobile-link">{{
          $t("landing.nav.packages")
        }}</a>
        <a href="#reviews" class="landing-nav__mobile-link">{{
          $t("landing.nav.reviews")
        }}</a>
        <div class="landing-nav__mobile-cta">
          <router-link to="/login" class="landing-nav__login landing-nav__login--block">{{
            $t("landing.nav.login")
          }}</router-link>
          <router-link to="/register" class="ps-btn ps-btn--lime landing-nav__register landing-nav__register--block">{{
            $t("landing.nav.register")
          }}</router-link>
        </div>
      </div>
    </header>

    <!-- ============ HERO ============ -->
    <section class="landing-hero">
      <div class="landing-hero__glow landing-hero__glow--brand"></div>
      <div class="landing-hero__glow landing-hero__glow--lime"></div>
      <div class="wrap landing-hero__grid">
        <div class="reveal landing-hero__copy">
          <span class="sticker landing-hero__sticker landing-hero__sticker--lao">{{
            $t("landing.hero.stickerLao")
          }}</span>
          <span class="sticker sticker-r landing-hero__sticker landing-hero__sticker--support">{{
            $t("landing.hero.stickerSupport")
          }}</span>
          <h1 class="landing-hero__title">
            {{ $t("landing.hero.title1") }}<br />
            <span class="landing-hero__highlight">{{
              $t("landing.hero.title2")
            }}</span
            ><br />
            <span class="outline-text">{{ $t("landing.hero.title3") }}</span>
          </h1>
          <p class="landing-hero__sub">{{ $t("landing.hero.sub") }}</p>

          <div class="landing-hero__cta">
            <router-link to="/register" class="ps-btn ps-btn--brand landing-hero__cta-primary">
              {{ $t("landing.hero.ctaFree") }}
              <v-icon size="16">fal fa-arrow-right</v-icon>
            </router-link>
            <a href="#packages" class="landing-hero__cta-secondary">{{
              $t("landing.hero.ctaPrice")
            }}</a>
          </div>

          <div class="landing-hero__checks">
            <span class="landing-hero__check">
              <span class="landing-hero__check-ic"
                ><v-icon size="12">fal fa-check</v-icon></span
              >
              {{ $t("landing.hero.check1") }}
            </span>
            <span class="landing-hero__check">
              <span class="landing-hero__check-ic"
                ><v-icon size="12">fal fa-check</v-icon></span
              >
              {{ $t("landing.hero.check2") }}
            </span>
            <span class="landing-hero__check">
              <span class="landing-hero__check-ic"
                ><v-icon size="12">fal fa-check</v-icon></span
              >
              {{ $t("landing.hero.check3") }}
            </span>
          </div>
        </div>

        <!-- polaroid screenshot -->
        <div class="reveal landing-hero__visual">
          <div class="landing-hero__polaroid">
            <img src="/assets/med/overview.jpg" alt="PosSmart" class="landing-hero__polaroid-img" />
            <p class="landing-hero__polaroid-cap">{{
              $t("landing.hero.polaroid")
            }}</p>
          </div>
          <div class="landing-hero__stat landing-hero__stat--brand">
            <span class="landing-hero__stat-num">500+</span>
            <span class="landing-hero__stat-label">{{
              $t("landing.hero.statStoresLabel")
            }}</span>
          </div>
          <div class="landing-hero__stat landing-hero__stat--lime">
            <span class="landing-hero__stat-num">10K+</span>
            <span class="landing-hero__stat-label">{{
              $t("landing.hero.statOrdersLabel")
            }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ STORES ============ -->
    <section id="business" class="landing-section landing-section--white">
      <div class="wrap">
        <div class="landing-head-row">
          <div>
            <p class="ps-eyebrow">{{ $t("landing.stores.label") }}</p>
            <h2 class="ps-title">{{ $t("landing.stores.title") }}</h2>
          </div>
          <p class="landing-head-row__sub">{{ $t("landing.stores.sub") }}</p>
        </div>
        <div class="landing-stores">
          <div
            v-for="s in stores"
            :key="s.name"
            class="landing-store card-hover"
          >
            <span
              class="landing-store__avatar"
              :style="{ backgroundColor: s.color }"
              >{{ s.initials }}</span
            >
            <p class="landing-store__name">
              {{ s.name }}<template v-if="s.sub"
                ><br /><span class="landing-store__sub">{{ s.sub }}</span></template
              >
            </p>
            <span class="landing-store__badge">{{ $t(s.badge) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ FEATURES ============ -->
    <section id="features" class="landing-section">
      <div class="wrap">
        <div class="landing-head-row landing-head-row--end">
          <div>
            <p class="ps-eyebrow">{{ $t("landing.features.label") }}</p>
            <h2 class="ps-title">
              {{ $t("landing.features.title") }}<br />
              <span class="outline-text">{{
                $t("landing.features.titleHighlight")
              }}</span>
            </h2>
          </div>
          <p class="landing-head-row__sub">{{ $t("landing.features.sub") }}</p>
        </div>
        <div class="landing-features">
          <div
            v-for="(f, i) in features"
            :key="i"
            class="landing-feature"
            :class="cardClass(i)"
          >
            <template v-if="i === 0">
              <div class="landing-feature__head">
                <span class="landing-feature__num landing-feature__num--dim"
                  >0{{ i + 1 }}</span
                >
                <span class="landing-feature__chip landing-feature__chip--white">{{
                  f.chip
                }}</span>
              </div>
            </template>
            <template v-else-if="i === 5">
              <div class="landing-feature__head">
                <span class="landing-feature__num landing-feature__num--dim"
                  >0{{ i + 1 }}</span
                >
                <img
                  src="/assets/play_store.png"
                  alt="Google Play"
                  class="landing-feature__play"
                />
              </div>
            </template>
            <template v-else-if="i === 6">
              <div class="landing-feature__head">
                <span class="landing-feature__num">0{{ i + 1 }}</span>
                <span class="landing-feature__chip landing-feature__chip--soft">{{
                  $t(f.chip)
                }}</span>
              </div>
            </template>
            <span v-else class="landing-feature__num" :class="numClass(i)"
              >0{{ i + 1 }}</span
            >
            <h3 class="landing-feature__title">{{ $t(f.title) }}</h3>
            <p class="landing-feature__desc" :class="descClass(i)">
              {{ $t(f.desc) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ SCREENS ============ -->
    <section id="screens" class="landing-section landing-section--soft">
      <div class="landing-section__glow"></div>
      <div class="wrap landing-section__rel">
        <div class="landing-center">
          <p class="ps-eyebrow">{{ $t("landing.screens.label") }}</p>
          <h2 class="ps-title">{{ $t("landing.screens.title") }}</h2>
          <p class="landing-center__sub">{{ $t("landing.screens.sub") }}</p>
        </div>

        <div class="landing-screens">
          <!-- thumbnail rail -->
          <div class="landing-screens__rail">
            <button
              v-for="(tb, i) in tabs"
              :key="i"
              class="landing-screens__tab"
              :class="{ 'tab-btn active': activeTab === i }"
              @click="activeTab = i"
            >
              <span class="landing-screens__thumb">
                <img :src="tb.img" alt="" loading="lazy" />
                <span class="landing-screens__thumb-num">0{{ i + 1 }}</span>
              </span>
              <span class="landing-screens__tab-text">
                <span class="landing-screens__tab-gray">{{
                  $t(tb.gray)
                }}</span>
                <span class="landing-screens__tab-bold">{{ $t(tb.bold) }}</span>
              </span>
            </button>
          </div>

          <!-- featured browser window -->
          <div class="landing-screens__window">
            <div class="landing-screens__bar">
              <span class="landing-screens__dot landing-screens__dot--red"></span>
              <span class="landing-screens__dot landing-screens__dot--amber"></span>
              <span class="landing-screens__dot landing-screens__dot--green"></span>
              <span class="landing-screens__url">
                <v-icon size="12">fal fa-lock</v-icon>
                pos.devla.la<span class="landing-screens__url-path">{{
                  tabs[activeTab].path
                }}</span>
                <span class="landing-screens__live"
                  ><span class="landing-screens__live-dot"></span
                  >{{ $t("landing.screens.live") }}</span
                >
              </span>
            </div>
            <div class="landing-screens__body">
              <img
                :src="tabs[activeTab].img"
                alt="PosSmart"
                loading="lazy"
              />
            </div>
            <div class="landing-screens__foot">
              <p class="landing-screens__cap">{{ $t(tabs[activeTab].cap) }}</p>
              <span class="landing-screens__chip">{{
                $t(tabs[activeTab].chip)
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ PRICING ============ -->
    <section id="packages" class="landing-section">
      <div class="wrap">
        <div class="landing-center">
          <p class="ps-eyebrow">{{ $t("landing.pricing.label") }}</p>
          <h2 class="ps-title">
            {{ $t("landing.pricing.title") }}<br />
            <span class="landing-pricing__title-highlight">{{
              $t("landing.pricing.titleHighlight")
            }}</span>
          </h2>
          <div class="landing-pricing__toggle">
            <button
              class="landing-pricing__toggle-btn"
              :class="{ 'landing-pricing__toggle-btn--active': !yearly }"
              @click="yearly = false"
            >
              {{ $t("landing.pricing.monthly") }}
            </button>
            <button
              class="landing-pricing__toggle-btn"
              :class="{ 'landing-pricing__toggle-btn--active': yearly }"
              @click="yearly = true"
            >
              {{ $t("landing.pricing.yearly") }}
              <span class="landing-pricing__save">{{
                $t("landing.pricing.save")
              }}</span>
            </button>
          </div>
        </div>

        <div class="landing-plans">
          <div
            v-for="p in plans"
            :key="p.name"
            class="landing-plan card-hover"
            :class="{ 'landing-plan--featured': p.featured }"
          >
            <span
              v-if="p.featured"
              class="sticker landing-plan__popular"
              >{{ $t("landing.pricing.popular") }}</span
            >
            <h3 class="landing-plan__name">{{ $t(p.name) }}</h3>
            <p class="landing-plan__desc" :class="{ 'landing-plan__desc--onbrand': p.featured }">{{
              $t(p.desc)
            }}</p>
            <div class="landing-plan__price">
              <span class="landing-plan__cur" :class="{ 'landing-plan__cur--onbrand': p.featured }">₭</span>
              <span class="landing-plan__amount">{{
                yearly ? p.yearlyPrice : p.monthly
              }}</span>
              <span class="landing-plan__per" :class="{ 'landing-plan__per--onbrand': p.featured }">{{
                $t("landing.pricing.perMonth")
              }}</span>
            </div>
            <ul class="landing-plan__features" :class="{ 'landing-plan__features--onbrand': p.featured }">
              <li v-for="f in p.features" :key="f" class="landing-plan__feature">
                <span class="landing-plan__check" :class="{ 'landing-plan__check--onbrand': p.featured }"
                  ><v-icon size="12">fal fa-check</v-icon></span
                >
                {{ $t(f) }}
              </li>
            </ul>
            <router-link
              to="/register"
              class="landing-plan__cta"
              :class="p.featured ? 'landing-plan__cta--lime' : 'landing-plan__cta--ghost'"
            >
              {{ $t("landing.pricing.cta") }}
            </router-link>
          </div>
        </div>
        <p class="landing-pricing__footnote">
          <span class="landing-pricing__footnote-box"
            >• {{ $t("landing.pricing.footnote") }}</span
          >
        </p>
      </div>
    </section>

    <!-- ============ REVIEWS ============ -->
    <section id="reviews" class="landing-section landing-section--white">
      <div class="wrap">
        <div class="landing-center">
          <p class="ps-eyebrow">{{ $t("landing.reviews.label") }}</p>
          <h2 class="ps-title">{{ $t("landing.reviews.title") }}</h2>
        </div>
        <div class="landing-reviews">
          <div
            v-for="r in reviews"
            :key="r.name"
            class="landing-review card-hover"
          >
            <div class="landing-review__stars">
              <v-icon v-for="n in 5" :key="n" size="16" color="#f59e0b"
                >fal fa-star</v-icon
              >
            </div>
            <p class="landing-review__text">{{ $t(r.text) }}</p>
            <div class="landing-review__author">
              <span class="landing-review__avatar">{{
                r.name
                  .split(" ")
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2)
              }}</span>
              <div>
                <p class="landing-review__name">{{ r.name }}</p>
                <p class="landing-review__role">{{ $t(r.role) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ FAQ ============ -->
    <section id="faq" class="landing-section landing-section--faq">
      <div class="landing-faq">
        <div class="landing-center">
          <p class="ps-eyebrow">{{ $t("landing.faq.label") }}</p>
          <h2 class="ps-title">{{ $t("landing.faq.title") }}</h2>
        </div>
        <div class="landing-faq__list">
          <div
            v-for="(f, i) in faqs"
            :key="i"
            class="faq-item landing-faq__item"
            :class="{ open: openFaq === i }"
          >
            <button
              class="landing-faq__q"
              @click="openFaq = openFaq === i ? null : i"
            >
              {{ $t(f.q) }}
              <v-icon size="16" class="faq-chevron"
                >fal fa-chevron-down</v-icon
              >
            </button>
            <div class="faq-a">
              <p class="landing-faq__a">{{ $t(f.a) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ CTA ============ -->
    <section class="landing-section landing-section--cta">
      <div class="wrap">
        <div class="landing-cta">
          <div class="landing-cta__glow landing-cta__glow--white"></div>
          <div class="landing-cta__glow landing-cta__glow--lime"></div>
          <div class="landing-cta__inner">
            <span class="sticker landing-cta__sticker">{{
              $t("landing.cta.sticker")
            }}</span>
            <h2 class="landing-cta__title">
              {{ $t("landing.cta.title1") }}<br />{{
                $t("landing.cta.title2")
              }}
            </h2>
            <p class="landing-cta__sub">{{ $t("landing.cta.sub") }}</p>
            <div class="landing-cta__buttons">
              <router-link to="/register" class="landing-cta__btn-free">{{
                $t("landing.cta.btnFree")
              }}</router-link>
              <router-link to="/contact_us" class="landing-cta__btn-contact">{{
                $t("landing.cta.btnContact")
              }}</router-link>
            </div>
            <p class="landing-cta__footnote">{{ $t("landing.cta.footnote") }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ FOOTER ============ -->
    <footer class="landing-footer">
      <div class="wrap landing-footer__grid">
        <div>
          <router-link to="/" class="landing-nav__logo">
            <img src="/images/pos.png" alt="PosSmart" class="landing-nav__mark" />
            <span class="landing-nav__name">PosSmart</span>
          </router-link>
          <p class="landing-footer__about">{{ $t("landing.footer.about") }}</p>
          <div class="landing-footer__social">
            <a
              href="https://www.facebook.com/pos.devla.la"
              class="landing-footer__social-btn"
              :aria-label="'Facebook'"
              ><v-icon size="14">fab fa-facebook-f</v-icon></a
            >
            <a
              href="https://www.tiktok.com/@possmart_devla"
              class="landing-footer__social-btn"
              :aria-label="'TikTok'"
              ><v-icon size="14">fal fa-music</v-icon></a
            >
            <a
              href="https://youtube.com/@possmart"
              class="landing-footer__social-btn"
              :aria-label="'YouTube'"
              ><v-icon size="14">fab fa-youtube</v-icon></a
            >
            <a
              href="https://wa.me/8562028492947"
              class="landing-footer__social-btn landing-footer__social-btn--green"
              :aria-label="'WhatsApp'"
              ><v-icon size="14">fab fa-whatsapp</v-icon></a
            >
          </div>
        </div>

        <div>
          <h4 class="landing-footer__title">{{
            $t("landing.footer.quickTitle")
          }}</h4>
          <ul class="landing-footer__links">
            <li v-for="l in quickLinks" :key="l.label">
              <component
                :is="l.to.startsWith('http') ? 'a' : 'router-link'"
                :href="l.to"
                :to="l.to"
                class="landing-footer__link"
                >{{ $t(l.label) }}</component
              >
            </li>
          </ul>
        </div>

        <div>
          <h4 class="landing-footer__title">{{
            $t("landing.footer.helpTitle")
          }}</h4>
          <ul class="landing-footer__links">
            <li v-for="l in helpLinks" :key="l.label">
              <component
                :is="l.to.startsWith('http') ? 'a' : 'router-link'"
                :href="l.to"
                :to="l.to"
                class="landing-footer__link"
                >{{ $t(l.label) }}</component
              >
            </li>
          </ul>
        </div>

        <div>
          <h4 class="landing-footer__title">{{
            $t("landing.footer.newsTitle")
          }}</h4>
          <p class="landing-footer__news-sub">{{
            $t("landing.footer.newsSub")
          }}</p>
          <form class="landing-footer__news" @submit.prevent="onNewsletter">
            <input
              type="email"
              required
              :placeholder="$t('landing.footer.newsPh')"
              class="ps-input"
            />
            <button type="submit" class="landing-footer__news-btn">{{
              $t("landing.footer.newsBtn")
            }}</button>
          </form>
          <div class="landing-footer__play">
            <img src="/assets/play_store.png" alt="Google Play" class="landing-footer__play-img" />
            <span class="landing-footer__play-text">{{
              $t("landing.footer.playStore")
            }}</span>
          </div>
        </div>
      </div>

      <div class="landing-footer__bottom">
        <div class="wrap landing-footer__bottom-inner">
          <p>{{ $t("landing.footer.copyright") }}</p>
          <div class="landing-footer__bottom-right">
            <ps-language-switcher direction="up" />
            <a href="https://pos.devla.la/privacy" class="landing-footer__link">{{
              $t("landing.footer.privacy")
            }}</a>
            <a href="https://pos.devla.la/terms" class="landing-footer__link">{{
              $t("landing.footer.terms")
            }}</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- ============ BACK TO TOP ============ -->
    <button
      class="landing-top"
      :class="showTop ? '' : 'landing-top--hidden'"
      :aria-label="$t('landing.backToTop')"
      @click="backToTop"
    >
      <v-icon size="18">fal fa-arrow-up</v-icon>
    </button>

    <!-- chat widget -->
    <ps-chat-widget />
  </div>
</template>

<script>
import { toast } from "@/controllers/alert";
import PsLanguageSwitcher from "@/components/landing/PsLanguageSwitcher.vue";
import PsChatWidget from "@/components/landing/PsChatWidget.vue";

export default {
  name: "LandingPage",
  layout: "blank",
  components: { PsLanguageSwitcher, PsChatWidget },
  data() {
    return {
      mobileMenuOpen: false,
      activeTab: 0,
      yearly: false,
      openFaq: 0,
      showTop: false,
      tabs: [
        { img: "/assets/med/overview.jpg", path: "/dashboard", gray: "landing.screens.tabs.0.gray", bold: "landing.screens.tabs.0.bold", cap: "landing.screens.tabs.0.cap", chip: "landing.screens.tabs.0.chip" },
        { img: "/assets/med/sale.jpg", path: "/pos", gray: "landing.screens.tabs.1.gray", bold: "landing.screens.tabs.1.bold", cap: "landing.screens.tabs.1.cap", chip: "landing.screens.tabs.1.chip" },
        { img: "/assets/med/products.jpg", path: "/products", gray: "landing.screens.tabs.2.gray", bold: "landing.screens.tabs.2.bold", cap: "landing.screens.tabs.2.cap", chip: "landing.screens.tabs.2.chip" },
        { img: "/assets/med/payment.jpg", path: "/payment", gray: "landing.screens.tabs.3.gray", bold: "landing.screens.tabs.3.bold", cap: "landing.screens.tabs.3.cap", chip: "landing.screens.tabs.3.chip" },
        { img: "/assets/med/discount.jpg", path: "/discounts", gray: "landing.screens.tabs.4.gray", bold: "landing.screens.tabs.4.bold", cap: "landing.screens.tabs.4.cap", chip: "landing.screens.tabs.4.chip" },
        { img: "/assets/med/report.jpg", path: "/reports", gray: "landing.screens.tabs.5.gray", bold: "landing.screens.tabs.5.bold", cap: "landing.screens.tabs.5.cap", chip: "landing.screens.tabs.5.chip" },
        { img: "/assets/med/settings.jpg", path: "/settings", gray: "landing.screens.tabs.6.gray", bold: "landing.screens.tabs.6.bold", cap: "landing.screens.tabs.6.cap", chip: "landing.screens.tabs.6.chip" },
        { img: "/assets/med/login.jpg", path: "/login", gray: "landing.screens.tabs.7.gray", bold: "landing.screens.tabs.7.bold", cap: "landing.screens.tabs.7.cap", chip: "landing.screens.tabs.7.chip" },
      ],
      plans: [
        { name: "landing.pricing.plans.0.name", desc: "landing.pricing.plans.0.desc", monthly: "360,000", yearlyPrice: "324,000", features: ["landing.pricing.plans.0.features.0", "landing.pricing.plans.0.features.1", "landing.pricing.plans.0.features.2"], featured: false },
        { name: "landing.pricing.plans.1.name", desc: "landing.pricing.plans.1.desc", monthly: "490,000", yearlyPrice: "441,000", features: ["landing.pricing.plans.1.features.0", "landing.pricing.plans.1.features.1", "landing.pricing.plans.1.features.2"], featured: true },
        { name: "landing.pricing.plans.2.name", desc: "landing.pricing.plans.2.desc", monthly: "750,000", yearlyPrice: "675,000", features: ["landing.pricing.plans.2.features.0", "landing.pricing.plans.2.features.1", "landing.pricing.plans.2.features.2"], featured: false },
      ],
      stores: [
        { initials: "BA", color: "#5b39c9", name: "BioActive LAOS", sub: "By KITAR", badge: "landing.stores.badges.supplement" },
        { initials: "TT", color: "#0ea5e9", name: "TT.Computer", badge: "landing.stores.badges.it" },
        { initials: "iC", color: "#f59e0b", name: "iCO Mart", badge: "landing.stores.badges.minimart" },
        { initials: "MD", color: "#d946ef", name: "MD Brand Name", badge: "landing.stores.badges.brand" },
        { initials: "JY", color: "#f43f5e", name: "JYOUY SHOP", badge: "landing.stores.badges.shopping" },
        { initials: "GE", color: "#10b981", name: "Golden Elephant", badge: "landing.stores.badges.shop" },
        { initials: "Cl", color: "#f97316", name: "Chern ly shop", badge: "landing.stores.badges.shop" },
        { initials: "ຣຄ", color: "#f59e0b", name: "ຣີຢູ່ນ້ຽນ ຄາເຟ່", badge: "landing.stores.badges.cafe" },
        { initials: "KM", color: "#0ea5e9", name: "Kitoh mobile", badge: "landing.stores.badges.mobile" },
        { initials: "七", color: "#16a34a", name: "七星农庄", badge: "landing.stores.badges.farm" },
        { initials: "PH", color: "#8b5cf6", name: "Paithong HQ", badge: "landing.stores.badges.office" },
        { initials: "Mai", color: "#ec4899", name: "ຮ້ານ Mai ເສື້ອຜ້າ", badge: "landing.stores.badges.fashion" },
        { initials: "ນຟ", color: "#f97316", name: "ນາງຟ້າ ອາຫານສົດ", badge: "landing.stores.badges.food" },
        { initials: "ສທ", color: "#ef4444", name: "ໂຮງໝໍເມືອງສີໂຄດຕະບອງ", badge: "landing.stores.badges.hospital" },
        { initials: "ກບ", color: "#14b8a6", name: "ກ່ນຍບໍ່ປີ່", badge: "landing.stores.badges.shop" },
      ],
      features: [
        { chip: "LAK · USD · THB", title: "landing.features.items.0.title", desc: "landing.features.items.0.desc" },
        { title: "landing.features.items.1.title", desc: "landing.features.items.1.desc" },
        { title: "landing.features.items.2.title", desc: "landing.features.items.2.desc" },
        { title: "landing.features.items.3.title", desc: "landing.features.items.3.desc" },
        { title: "landing.features.items.4.title", desc: "landing.features.items.4.desc" },
        { title: "landing.features.items.5.title", desc: "landing.features.items.5.desc" },
        { chip: "landing.features.items.6.chip", title: "landing.features.items.6.title", desc: "landing.features.items.6.desc" },
        { title: "landing.features.items.7.title", desc: "landing.features.items.7.desc" },
      ],
      faqs: [
        { q: "landing.faq.items.0.q", a: "landing.faq.items.0.a" },
        { q: "landing.faq.items.1.q", a: "landing.faq.items.1.a" },
        { q: "landing.faq.items.2.q", a: "landing.faq.items.2.a" },
        { q: "landing.faq.items.3.q", a: "landing.faq.items.3.a" },
        { q: "landing.faq.items.4.q", a: "landing.faq.items.4.a" },
      ],
      reviews: [
        { text: "landing.reviews.items.0.text", name: "Somdee Douangdy", role: "landing.reviews.items.0.role" },
        { text: "landing.reviews.items.1.text", name: "Vilay Souksavath", role: "landing.reviews.items.1.role" },
        { text: "landing.reviews.items.2.text", name: "Khamphay Sengthong", role: "landing.reviews.items.2.role" },
      ],
      quickLinks: [
        { label: "landing.footer.quick.0", to: "/" },
        { label: "landing.footer.quick.1", to: "#features" },
        { label: "landing.footer.quick.2", to: "#packages" },
        { label: "landing.footer.quick.3", to: "https://pos.devla.la/tutorials" },
      ],
      helpLinks: [
        { label: "landing.footer.help.0", to: "https://pos.devla.la/faq" },
        { label: "landing.footer.help.1", to: "https://pos.devla.la/help" },
        { label: "landing.footer.help.2", to: "/contact_us" },
        { label: "landing.footer.help.3", to: "/login" },
      ],
    };
  },
  mounted() {
    this.setupRevealObserver();
    window.addEventListener("scroll", this.onScroll, { passive: true });
  },
  beforeDestroy() {
    if (this.io) this.io.disconnect();
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    setupRevealObserver() {
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("visible");
              this.io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.08 }
      );
      this.$nextTick(() => {
        document.querySelectorAll(".reveal").forEach((el) => this.io.observe(el));
      });
    },
    onScroll() {
      this.showTop = window.scrollY > 500;
    },
    backToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    cardClass(i) {
      if (i === 0) return "landing-feature--brand landing-feature--span2";
      if (i === 1) return "landing-feature--lime";
      if (i === 5) return "landing-feature--brand landing-feature--span2";
      if (i === 6) return "landing-feature--white landing-feature--span2";
      if (i === 7) return "landing-feature--white landing-feature--span2";
      return "landing-feature--white";
    },
    numClass(i) {
      if (i === 0) return "landing-feature__num--dim";
      if (i === 1 || i === 5) return "landing-feature__num--dim";
      return "landing-feature__num--brand";
    },
    descClass(i) {
      if (i === 0 || i === 5) return "landing-feature__desc--onbrand";
      if (i === 1) return "landing-feature__desc--onlime";
      return "landing-feature__desc--muted";
    },
    async onNewsletter() {
      await toast(this, this.$t("landing.newsletterToast"));
    },
  },
};
</script>

<style scoped>
/* ============ shared ============ */
.wrap {
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1rem;
}
@media (min-width: 640px) {
  .wrap {
    padding: 0 1.5rem;
  }
}
@media (min-width: 1024px) {
  .wrap {
    padding: 0 2rem;
  }
}

.landing-section {
  padding: 4rem 0;
}
@media (min-width: 1024px) {
  .landing-section {
    padding: 5rem 0;
  }
}
.landing-section--white {
  border-top: 1px solid var(--ps-slate-300);
  border-bottom: 1px solid var(--ps-slate-300);
  background: #fff;
}
.landing-section--soft {
  border-top: 1px solid var(--ps-slate-300);
  border-bottom: 1px solid var(--ps-slate-300);
  background: var(--ps-brand-soft);
  position: relative;
  overflow: hidden;
}
.landing-section--faq {
  max-width: 48rem;
  margin: 0 auto;
  /* No .wrap inside this section — keep its own side padding so the
     accordion never touches the screen edges on phones. */
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 5rem;
}
@media (min-width: 640px) {
  .landing-section--faq {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .landing-section--faq {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}
.landing-section--cta {
  padding-top: 1rem;
}
.landing-section__glow {
  position: absolute;
  top: -6rem;
  right: -6rem;
  width: 24rem;
  height: 24rem;
  border-radius: 50%;
  background: rgba(91, 57, 201, 0.1);
  filter: blur(72px);
  pointer-events: none;
}
.landing-section__rel {
  position: relative;
}

.landing-head-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
@media (min-width: 1024px) {
  .landing-head-row {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
}
.landing-head-row--end {
  align-items: flex-start;
}
.landing-head-row__sub {
  max-width: 24rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-500);
}

.landing-center {
  max-width: 42rem;
  margin: 0 auto;
  text-align: center;
}
.landing-center__sub {
  margin: 1rem auto 0;
  max-width: 28rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--ps-slate-500);
}

/* ============ ticker ============ */
.landing-ticker {
  background: var(--ps-lime);
  color: var(--ps-ink);
  font-size: 0.875rem;
  font-weight: 700;
  padding: 10px 0;
  border-bottom: 1px solid var(--ps-slate-300);
  overflow: hidden;
}
.landing-ticker__star {
  color: var(--ps-brand);
}

/* ============ nav ============ */
.landing-nav {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--ps-cream);
  border-bottom: 1px solid var(--ps-slate-300);
}
.landing-nav__inner {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.landing-nav__logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--ps-ink);
}
.landing-nav__mark {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #141021;
  box-shadow: var(--ps-shadow-hard);
  object-fit: cover;
  display: block;
}
.landing-nav__name {
  font-weight: 900;
  font-size: 20px;
  letter-spacing: -0.02em;
}
.landing-nav__links {
  display: none;
  align-items: center;
  gap: 24px;
  font-size: 15px;
  font-weight: 700;
}
.landing-nav__link {
  color: var(--ps-ink);
  text-decoration: none;
  transition: color 0.15s ease;
}
.landing-nav__link:hover {
  color: var(--ps-brand);
}
.landing-nav__cta {
  display: none;
  align-items: center;
  gap: 12px;
}
.landing-nav__login {
  padding: 8px 16px;
  border: 1px solid #141021;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--ps-ink);
  text-decoration: none;
  transition: background-color 0.15s ease;
}
.landing-nav__login:hover {
  background: #fff;
}
.landing-nav__register {
  padding: 10px 20px;
  font-size: 0.875rem;
  border-radius: 12px; /* rounded-xl like the reference */
  text-decoration: none;
}
.landing-nav__register--block {
  display: flex;
  flex: 1;
  justify-content: center;
}
.landing-nav__login--block {
  display: block;
  text-align: center;
  flex: 1;
}
.landing-nav__burger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #141021;
  border-radius: 8px;
  background: transparent;
  color: var(--ps-ink);
}
/* Desktop nav only from lg up: logo + 5 links + 2 CTAs overflow a md
   (768–1023px) viewport, especially with Lao labels — keep the burger there. */
@media (min-width: 1024px) {
  .landing-nav__links,
  .landing-nav__cta {
    display: flex;
  }
  .landing-nav__burger {
    display: none;
  }
}
.landing-nav__mobile {
  border-top: 1px solid var(--ps-slate-300);
  background: var(--ps-cream);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
@media (min-width: 1024px) {
  .landing-nav__mobile {
    display: none;
  }
}
.landing-nav__mobile-link {
  padding: 10px 0;
  font-weight: 700;
  color: var(--ps-ink);
  text-decoration: none;
}
.landing-nav__mobile-cta {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

/* ============ hero ============ */
.landing-hero {
  position: relative;
  overflow: hidden;
}
.landing-hero__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(72px);
  pointer-events: none;
}
.landing-hero__glow--brand {
  top: 4rem;
  right: 0;
  width: 18rem;
  height: 18rem;
  background: rgba(91, 57, 201, 0.1);
}
.landing-hero__glow--lime {
  bottom: 0;
  left: 0;
  width: 18rem;
  height: 18rem;
  background: rgba(163, 230, 53, 0.2);
}
.landing-hero__grid {
  padding-top: 3.5rem;
  padding-bottom: 4rem;
  display: grid;
  gap: 3.5rem;
  align-items: center;
}
@media (min-width: 1024px) {
  .landing-hero__grid {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    padding-top: 5rem;
    padding-bottom: 6rem;
  }
}
.landing-hero__copy {
  position: relative;
}
.landing-hero__sticker {
  position: absolute;
  font-weight: 900;
  font-size: 0.875rem;
  padding: 8px 16px;
  border: 1px solid #141021;
  border-radius: 12px;
  box-shadow: var(--ps-shadow-hard);
  z-index: 1;
}
.landing-hero__sticker--lao {
  top: -24px;
  left: -12px;
  background: var(--ps-brand);
  color: #fff;
}
.landing-hero__sticker--support {
  top: 64px;
  right: -8px;
  background: var(--ps-lime);
  color: var(--ps-ink);
}
/* On phones the absolutely-positioned stickers sit on top of the headline
   text — render them as in-flow chips above the title instead. */
@media (max-width: 639px) {
  .landing-hero__sticker {
    position: static;
    display: inline-block;
    margin: 0 8px 16px 0;
  }
  .landing-hero__sticker--lao,
  .landing-hero__sticker--support {
    top: auto;
    right: auto;
  }
  .landing-hero__title {
    margin-top: 0;
  }
}
.landing-hero__title {
  margin: 32px 0 0;
  font-size: clamp(2.4rem, 5vw, 4.2rem);
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--ps-ink);
  overflow-wrap: break-word; /* long unbroken Lao/brand words must not push width */
}
.landing-hero__highlight {
  position: relative;
  display: inline-block;
}
.landing-hero__highlight::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 0.65em;
  background: var(--ps-lime);
  z-index: -1;
}
.landing-hero__sub {
  margin: 24px 0 0;
  max-width: 32rem;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.7;
  color: var(--ps-slate-600);
}
.landing-hero__cta {
  margin-top: 36px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.landing-hero__cta-primary {
  padding: 1rem 2rem;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: var(--ps-shadow-hard); /* reference uses ink hard shadow */
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    background-color 0.15s ease;
}
.landing-hero__cta-primary:hover {
  transform: translate(-2px, -2px);
  box-shadow: 3px 3px 0 0 #141021;
  background: var(--ps-brand-dark);
}
.landing-hero__cta-secondary {
  display: inline-flex;
  align-items: center;
  padding: 1rem 2rem;
  border: 1px solid #141021;
  border-radius: 1rem;
  background: #fff;
  font-weight: 700;
  font-size: 1rem;
  color: var(--ps-ink);
  text-decoration: none;
  transition: background-color 0.15s ease;
}
.landing-hero__cta-secondary:hover {
  background: var(--ps-brand-soft);
}
.landing-hero__checks {
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--ps-slate-600);
}
.landing-hero__check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.landing-hero__check-ic {
  width: 20px;
  height: 20px;
  background: var(--ps-lime);
  border: 1px solid #141021;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ps-ink);
}
.landing-hero__visual {
  position: relative;
}
.landing-hero__polaroid {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 16px;
  box-shadow: var(--ps-shadow-hardlg);
  padding: 1rem;
  transform: rotate(1.5deg);
}
.landing-hero__polaroid-img {
  width: 100%;
  display: block;
  border-radius: 8px;
}
.landing-hero__polaroid-cap {
  padding: 12px 0 4px;
  margin: 0;
  text-align: center;
  font-weight: 900;
  font-size: 0.875rem;
}
.landing-hero__stat {
  position: absolute;
  padding: 12px 20px;
  border: 1px solid #141021;
  border-radius: 12px;
  box-shadow: var(--ps-shadow-hard);
  font-weight: 900;
  display: flex;
  flex-direction: column;
}
.landing-hero__stat--brand {
  bottom: -32px;
  left: -16px;
  background: var(--ps-brand);
  color: #fff;
  transform: rotate(-3deg);
}
.landing-hero__stat--lime {
  top: -24px;
  right: -8px;
  background: var(--ps-lime);
  color: var(--ps-ink);
  transform: rotate(4deg);
}
@media (min-width: 640px) {
  .landing-hero__stat--brand {
    left: -32px;
  }
  .landing-hero__stat--lime {
    right: -24px;
  }
}
.landing-hero__stat-num {
  font-size: 30px;
}
.landing-hero__stat-label {
  font-size: 0.875rem;
  font-weight: 700;
}

/* ============ stores ============ */
.landing-stores {
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}
@media (min-width: 640px) {
  .landing-stores {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (min-width: 1024px) {
  .landing-stores {
    grid-template-columns: repeat(5, 1fr);
  }
}
.landing-store {
  background: var(--ps-cream);
  border: 1px solid #141021;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 2px 2px 0 0 #141021;
}
.landing-store__avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  color: #fff;
  font-weight: 900;
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.landing-store__name {
  margin: 12px 0 0;
  font-weight: 900;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--ps-ink);
  overflow-wrap: anywhere; /* store names can be one long Lao string */
}
.landing-store__sub {
  font-weight: 700;
  color: var(--ps-slate-500);
}
.landing-store__badge {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 900;
  color: #166534;
  background: #d1fae5;
  border: 2px solid #141021;
  border-radius: 999px;
  padding: 2px 10px;
}

/* ============ features ============ */
.landing-features {
  margin-top: 40px;
  display: grid;
  gap: 20px;
}
@media (min-width: 640px) {
  .landing-features {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 1024px) {
  .landing-features {
    grid-template-columns: repeat(4, 1fr);
  }
}
.landing-feature {
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 32px;
}
/* Single column below sm: "span 2" there would create a phantom second
   column and shrink the other cards to half width, so only span from sm up. */
.landing-feature--span2 {
  grid-column: span 1;
}
@media (min-width: 640px) {
  .landing-feature--span2 {
    grid-column: span 2;
  }
}
.landing-feature--brand {
  background: var(--ps-brand);
  color: #fff;
}
.landing-feature--lime {
  background: var(--ps-lime);
  color: var(--ps-ink);
}
.landing-feature--white {
  background: #fff;
  color: var(--ps-ink);
}
.landing-feature__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.landing-feature__num {
  font-size: 48px;
  font-weight: 900;
}
.landing-feature__num--dim {
  opacity: 0.2;
  color: #fff;
}
.landing-feature__num--brand {
  color: var(--ps-brand);
}
.landing-feature__chip {
  font-size: 11px;
  font-weight: 900;
  border-radius: 999px;
  padding: 4px 12px;
  white-space: nowrap;
}
.landing-feature__chip--white {
  background: #fff;
  color: var(--ps-brand);
}
.landing-feature__chip--soft {
  background: var(--ps-brand-soft);
  color: var(--ps-brand);
}
.landing-feature__play {
  height: 40px;
}
.landing-feature__title {
  margin: 16px 0 0;
  font-size: 1.5rem;
  font-weight: 900;
  color: inherit;
}
.landing-feature__desc {
  margin: 8px 0 0;
  line-height: 1.7;
  font-weight: 500;
}
.landing-feature__desc--onbrand {
  color: rgba(255, 255, 255, 0.85);
}
.landing-feature__desc--onlime {
  color: rgba(20, 16, 33, 0.7);
}
.landing-feature__desc--muted {
  color: var(--ps-slate-500);
}

/* ============ screens ============ */
.landing-screens {
  margin-top: 48px;
  display: grid;
  gap: 32px;
  align-items: start;
}
@media (min-width: 1024px) {
  .landing-screens {
    grid-template-columns: 360px 1fr;
  }
}
.landing-screens__rail {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
}
@media (min-width: 1024px) {
  .landing-screens__rail {
    flex-direction: column;
    overflow: visible;
  }
}
.landing-screens__tab {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid #141021;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.15s ease;
}
.landing-screens__tab:hover {
  border-color: var(--ps-brand);
}
.landing-screens__thumb {
  position: relative;
  width: 80px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--ps-slate-300);
  flex-shrink: 0;
}
.landing-screens__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.landing-screens__thumb-num {
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  border-bottom-right-radius: 8px;
  background: var(--ps-brand);
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.landing-screens__tab-text {
  display: flex;
  flex-direction: column;
}
.landing-screens__tab-gray {
  font-size: 11px;
  font-weight: 700;
  color: var(--ps-slate-400);
}
.landing-screens__tab-bold {
  font-weight: 900;
  font-size: 0.875rem;
  color: var(--ps-ink);
}
.landing-screens__window {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 16px;
  box-shadow: var(--ps-shadow-hard);
  overflow: hidden;
  position: sticky;
  top: 96px;
}
.landing-screens__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--ps-slate-200);
  background: var(--ps-slate-100);
}
.landing-screens__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.landing-screens__dot--red {
  background: #f87171;
}
.landing-screens__dot--amber {
  background: #fbbf24;
}
.landing-screens__dot--green {
  background: #34d399;
}
.landing-screens__url {
  flex: 1;
  margin: 0 8px;
  height: 28px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid var(--ps-slate-200);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--ps-slate-500);
  min-width: 0;
}
.landing-screens__url-path {
  color: var(--ps-brand);
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.landing-screens__live {
  display: none;
  align-items: center;
  gap: 4px;
  color: #059669;
  font-weight: 900;
  flex-shrink: 0;
}
@media (min-width: 640px) {
  .landing-screens__live {
    display: inline-flex;
  }
}
.landing-screens__live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  animation: ps-pulse-dot 1.5s infinite;
}
@keyframes ps-pulse-dot {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
.landing-screens__body {
  padding: 8px 12px;
  background: var(--ps-slate-100);
}
.landing-screens__body img {
  width: 100%;
  display: block;
  border-radius: 8px;
}
.landing-screens__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
}
.landing-screens__cap {
  font-weight: 900;
  font-size: 0.875rem;
  margin: 0;
}
.landing-screens__chip {
  display: none;
  align-items: center;
  font-size: 11px;
  font-weight: 900;
  color: var(--ps-brand);
  background: var(--ps-brand-soft);
  border: 1px solid #141021;
  border-radius: 999px;
  padding: 4px 12px;
  flex-shrink: 0;
}
@media (min-width: 640px) {
  .landing-screens__chip {
    display: inline-flex;
  }
}

/* ============ pricing ============ */
.landing-pricing__title-highlight {
  background: var(--ps-lime);
  border: 1px solid #141021;
  border-radius: 8px;
  padding: 0 12px;
  display: inline-block;
}
.landing-pricing__toggle {
  margin-top: 24px;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 100%;
  background: #fff;
  border: 1px solid #141021;
  border-radius: 16px;
  padding: 6px;
}
.landing-pricing__toggle-btn {
  padding: 8px 20px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 900;
  border: none;
  background: transparent;
  color: var(--ps-slate-500);
}
.landing-pricing__toggle-btn:hover {
  color: var(--ps-ink);
}
.landing-pricing__toggle-btn--active {
  background: var(--ps-brand);
  color: #fff;
}
.landing-pricing__save {
  color: #059669;
  font-size: 12px;
  font-weight: 900;
}
.landing-plans {
  margin: 48px auto 0;
  max-width: 64rem;
  display: grid;
  gap: 32px;
  align-items: stretch;
}
@media (min-width: 768px) {
  .landing-plans {
    grid-template-columns: repeat(3, 1fr);
  }
}
.landing-plan {
  position: relative;
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 32px;
  background: #fff;
  box-shadow: var(--ps-shadow-hard);
  display: flex;
  flex-direction: column;
}
.landing-plan--featured {
  background: var(--ps-brand);
  color: #fff;
  box-shadow: var(--ps-shadow-hardlg);
}
@media (min-width: 768px) {
  .landing-plan--featured {
    margin: -16px 0;
  }
}
.landing-plan__popular {
  position: absolute;
  top: -16px;
  right: 24px;
  background: var(--ps-lime);
  color: var(--ps-ink);
  font-size: 12px;
  font-weight: 900;
  padding: 6px 16px;
  border: 1px solid #141021;
  border-radius: 999px;
}
.landing-plan__name {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 900;
}
.landing-plan__desc {
  margin: 4px 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-500);
}
.landing-plan__desc--onbrand {
  color: rgba(255, 255, 255, 0.8);
}
.landing-plan__price {
  margin-top: 20px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap; /* "₭ 750,000 /ດອນ" must not overflow a narrow card */
  gap: 4px;
}
.landing-plan__cur {
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--ps-slate-400);
}
.landing-plan__cur--onbrand {
  color: rgba(255, 255, 255, 0.5);
}
.landing-plan__amount {
  font-size: 48px;
  font-weight: 900;
  letter-spacing: -0.02em;
}
.landing-plan__per {
  font-weight: 700;
  color: var(--ps-slate-400);
}
.landing-plan__per--onbrand {
  color: rgba(255, 255, 255, 0.7);
}
.landing-plan__features {
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--ps-slate-600);
  flex: 1;
}
.landing-plan__features--onbrand {
  color: rgba(255, 255, 255, 0.95);
}
.landing-plan__feature {
  display: flex;
  align-items: center;
  gap: 10px;
}
.landing-plan__check {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: var(--ps-lime);
  border: 1px solid #141021;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ps-ink);
  flex-shrink: 0;
}
.landing-plan__check--onbrand {
  background: #fff;
  color: var(--ps-brand);
}
.landing-plan__cta {
  margin-top: 28px;
  display: block;
  text-align: center;
  padding: 12px 20px;
  border-radius: 16px;
  font-weight: 900;
  font-size: 0.875rem;
  text-decoration: none;
}
.landing-plan__cta--lime {
  background: var(--ps-lime);
  color: var(--ps-ink);
  border: 1px solid #141021;
  box-shadow: 2px 2px 0 0 #141021;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.landing-plan__cta--lime:hover {
  transform: translate(3px, 3px);
  box-shadow: none;
}
.landing-plan__cta--ghost {
  border: 1px solid #141021;
  color: var(--ps-ink);
  transition: background-color 0.15s ease, color 0.15s ease;
}
.landing-plan__cta--ghost:hover {
  background: var(--ps-ink);
  color: #fff;
}
.landing-pricing__footnote {
  margin-top: 40px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--ps-slate-500);
}
.landing-pricing__footnote-box {
  background: var(--ps-lime);
  border: 1px solid #141021;
  border-radius: 4px;
  padding: 2px 8px;
  display: inline-block;
}

/* ============ reviews ============ */
.landing-reviews {
  margin-top: 48px;
  display: grid;
  gap: 24px;
}
@media (min-width: 640px) {
  .landing-reviews {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 1024px) {
  .landing-reviews {
    grid-template-columns: repeat(3, 1fr);
  }
}
.landing-review {
  background: var(--ps-cream);
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 2px 2px 0 0 #141021;
}
.landing-review__stars {
  display: flex;
  gap: 2px;
}
.landing-review__text {
  margin: 16px 0 0;
  font-size: 0.9375rem;
  line-height: 1.7;
  font-weight: 500;
  color: var(--ps-slate-600);
}
.landing-review__author {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.landing-review__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--ps-brand);
  color: #fff;
  border: 1px solid #141021;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 0.875rem;
  flex-shrink: 0;
}
.landing-review__name {
  margin: 0;
  font-weight: 900;
  font-size: 0.875rem;
}
.landing-review__role {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--ps-slate-400);
}

/* ============ faq ============ */
.landing-faq__list {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.landing-faq__item {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 2px 2px 0 0 #141021;
}
.landing-faq__q {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  text-align: left;
  font-weight: 900;
  font-size: 1rem;
  color: var(--ps-ink);
  background: transparent;
  border: none;
  transition: background-color 0.15s ease;
}
.landing-faq__q:hover {
  background: var(--ps-cream);
}
.landing-faq__a {
  padding: 0 24px 20px;
  margin: 0;
  font-weight: 500;
  color: var(--ps-slate-500);
  line-height: 1.7;
  font-size: 0.875rem;
}

/* ============ cta ============ */
.landing-cta {
  position: relative;
  background: var(--ps-brand);
  color: #fff;
  border: 1px solid #141021;
  border-radius: 2rem;
  padding: 40px;
  text-align: center;
  overflow: hidden;
  box-shadow: var(--ps-shadow-hardlg);
}
@media (min-width: 1024px) {
  .landing-cta {
    padding: 64px;
  }
}
.landing-cta__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(72px);
  pointer-events: none;
}
.landing-cta__glow--white {
  top: -40px;
  left: -40px;
  width: 12rem;
  height: 12rem;
  background: rgba(255, 255, 255, 0.2);
}
.landing-cta__glow--lime {
  bottom: -40px;
  right: -40px;
  width: 12rem;
  height: 12rem;
  background: rgba(163, 230, 53, 0.25);
}
.landing-cta__inner {
  position: relative;
  max-width: 42rem;
  margin: 0 auto;
}
.landing-cta__sticker {
  display: inline-block;
  background: var(--ps-lime);
  color: var(--ps-ink);
  font-size: 0.875rem;
  font-weight: 900;
  padding: 8px 16px;
  border: 1px solid #141021;
  border-radius: 12px;
  margin-bottom: 24px;
}
.landing-cta__title {
  margin: 0;
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.08;
}
.landing-cta__sub {
  margin: 20px 0 0;
  font-size: 1.125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}
.landing-cta__buttons {
  margin-top: 36px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
}
.landing-cta__btn-free {
  background: var(--ps-lime);
  color: var(--ps-ink);
  font-weight: 900;
  padding: 16px 36px;
  border: 1px solid #141021;
  border-radius: 16px;
  box-shadow: 3px 3px 0 0 #3b1e8c;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.landing-cta__btn-free:hover {
  transform: translate(3px, 3px);
  box-shadow: none;
}
.landing-cta__btn-contact {
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.7);
  color: #fff;
  font-weight: 900;
  padding: 16px 36px;
  border-radius: 16px;
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.landing-cta__btn-contact:hover {
  background: #fff;
  color: var(--ps-brand-dark);
}
.landing-cta__footnote {
  margin: 20px 0 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
}

/* ============ footer ============ */
.landing-footer {
  background: var(--ps-cream);
  color: var(--ps-slate-500);
  border-top: 1px solid var(--ps-slate-300);
  font-size: 0.875rem;
}
.landing-footer__grid {
  padding-top: 56px;
  padding-bottom: 56px;
  display: grid;
  gap: 40px;
}
@media (min-width: 768px) {
  .landing-footer__grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
.landing-footer__about {
  margin: 16px 0 0;
  line-height: 1.7;
  font-weight: 500;
}
.landing-footer__social {
  margin-top: 20px;
  display: flex;
  gap: 12px;
}
.landing-footer__social-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #141021;
  color: var(--ps-ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.landing-footer__social-btn:hover {
  background: var(--ps-brand);
  color: #fff;
}
.landing-footer__social-btn--green:hover {
  background: #10b981;
}
.landing-footer__title {
  margin: 0 0 16px;
  font-weight: 900;
  color: var(--ps-ink);
}
.landing-footer__links {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-weight: 500;
}
.landing-footer__link {
  color: inherit;
  text-decoration: none;
  transition: color 0.15s ease;
}
.landing-footer__link:hover {
  color: var(--ps-brand);
}
.landing-footer__news-sub {
  margin: 0;
  font-weight: 500;
}
.landing-footer__news {
  margin-top: 16px;
  display: flex;
  gap: 8px;
}
/* Very narrow screens: stack the input above a full-width button */
@media (max-width: 420px) {
  .landing-footer__news {
    flex-wrap: wrap;
  }
  .landing-footer__news-btn {
    flex: 1 1 100%;
    justify-content: center;
  }
}
.landing-footer__news .ps-input {
  flex: 1;
  min-width: 0;
  padding: 10px 16px;
}
.landing-footer__news-btn {
  padding: 10px 16px;
  border-radius: 12px;
  background: var(--ps-lime);
  color: var(--ps-ink);
  font-weight: 900;
  font-size: 0.875rem;
  border: 1px solid #141021;
  transition: background-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}
.landing-footer__news-btn:hover {
  background: var(--ps-brand);
  color: #fff;
}
.landing-footer__play {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.landing-footer__play-img {
  height: 40px;
}
.landing-footer__play-text {
  font-size: 12px;
  color: var(--ps-slate-400);
  font-weight: 500;
  white-space: pre-line;
}
.landing-footer__bottom {
  border-top: 1px solid var(--ps-slate-300);
}
.landing-footer__bottom-inner {
  padding-top: 24px;
  padding-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  font-weight: 500;
}
@media (min-width: 640px) {
  .landing-footer__bottom-inner {
    flex-direction: row;
  }
}
.landing-footer__bottom-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* ============ back to top ============ */
.landing-top {
  position: fixed;
  bottom: 96px;
  right: 20px;
  z-index: 40;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #141021;
  box-shadow: 2px 2px 0 0 #141021;
  color: var(--ps-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease, transform 0.2s ease,
    background-color 0.15s ease, color 0.15s ease;
}
.landing-top:hover {
  background: var(--ps-brand);
  color: #fff;
}
.landing-top--hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(8px);
}
</style>
